import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { confirmAction } from '../../utils/confirmToast';
import { FileText, Plus, Download, Trash2, FileCheck } from 'lucide-react';
import PageHeader from '../../components/common/PageHeader/PageHeader';
import Drawer from '../../components/common/Drawer/Drawer';
import api from '../../api/axios';
import styles from './Documents.module.css';

const Documents = () => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);

  const [formData, setFormData] = useState({
    name: '',
    type: 'ID Proof',
    file: null
  });

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/users/documents');
      setDocuments(data);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch documents');
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFormData({ ...formData, file: e.target.files[0], name: formData.name || e.target.files[0].name });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.file) {
      toast.error("Please select a file to upload");
      return;
    }

    const uploadData = new FormData();
    uploadData.append('file', formData.file);
    uploadData.append('name', formData.name);
    uploadData.append('type', formData.type);

    try {
      const { data } = await api.post('/users/documents', uploadData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setDocuments([data, ...documents]);
      setFormData({ name: '', type: 'ID Proof', file: null });
      setIsDrawerOpen(false);
      toast.success('Document uploaded successfully!');
    } catch (error) {
      toast.error('Failed to upload document');
    }
  };

  const handleDelete = async (id) => {
    confirmAction("Are you sure you want to delete this document?", async () => {
      try {
        await api.delete(`/users/documents/${id}`);
        setDocuments(documents.filter(doc => doc._id !== id));
        toast.success('Document deleted successfully!');
      } catch (error) {
        toast.error('Failed to delete document');
      }
    });
  };

  return (
    <div className={styles.container}>
      <PageHeader 
        title="My Documents" 
        breadcrumb={['Resources', 'Documents']} 
        actionButton={
          <button className={styles.addBtn} onClick={() => setIsDrawerOpen(true)}>
            <Plus size={16} /> Upload Document
          </button>
        }
      />

      <div className={styles.grid}>
        {loading ? <p>Loading documents...</p> : documents.map(doc => (
          <div key={doc._id} className={styles.card}>
            <div className={styles.iconWrapper}>
              <FileCheck size={24} />
            </div>
            <div className={styles.docInfo}>
              <h3>{doc.name}</h3>
              <div className={styles.meta}>
                <span className={styles.badge}>{doc.type}</span>
                <span>{doc.size}</span>
              </div>
              <small>Uploaded on {new Date(doc.createdAt).toLocaleDateString('en-GB')}</small>
            </div>
            <div className={styles.actions}>
              <a href={`${import.meta.env.VITE_API_URL.replace(/\/api\/?$/, '')}${doc.filePath}`} target="_blank" rel="noopener noreferrer" className={styles.actionBtn}>
                <Download size={16} />
              </a>
              <button className={`${styles.actionBtn} ${styles.danger}`} onClick={() => handleDelete(doc._id)}>
                <Trash2 size={16} />
              </button>
            </div>
          </div>
        ))}

        <div className={styles.addCard} onClick={() => setIsDrawerOpen(true)}>
          <div className={styles.addIcon}>
            <Plus size={24} />
          </div>
          <span>Upload New File</span>
        </div>
      </div>

      <Drawer 
        isOpen={isDrawerOpen} 
        onClose={() => setIsDrawerOpen(false)} 
        title="Upload Document"
      >
        <form className="drawerForm" onSubmit={handleSubmit}>
          <div 
            className={styles.uploadArea} 
            onClick={() => document.getElementById('file-upload').click()}
            style={{ cursor: 'pointer' }}
          >
            <input 
              type="file" 
              id="file-upload" 
              style={{ display: 'none' }} 
              onChange={handleFileChange}
              accept=".pdf,.jpg,.jpeg,.png"
            />
            <FileText size={32} color="#9ca3af" />
            <p>{formData.file ? formData.file.name : "Click or drag file to this area to upload"}</p>
            <small>Support for a single or bulk upload. Max size 5MB.</small>
          </div>

          <div className="formGroup">
            <label>Document Name</label>
            <input name="name" value={formData.name} onChange={handleChange} required placeholder="e.g. Passport, Lease Agreement" />
          </div>
          <div className="formGroup">
            <label>Document Category</label>
            <select name="type" value={formData.type} onChange={handleChange}>
              <option value="ID Proof">ID Proof</option>
              <option value="Property">Property / Agreement</option>
              <option value="Verification">Police Verification</option>
              <option value="Vehicle">Vehicle RC</option>
              <option value="Other">Other</option>
            </select>
          </div>
          
          <div className="formActions">
            <button type="button" className="btnCancel" onClick={() => setIsDrawerOpen(false)}>Cancel</button>
            <button type="submit" className="btnSubmit">Upload File</button>
          </div>
        </form>
      </Drawer>
    </div>
  );
};

export default Documents;
